import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TextInput } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../theme/colors';
import { spacing } from '../theme/spacing';
import { typography } from '../theme/typography';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useTypography } from '../hooks/useTypography';
import { FlowSessionItem } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';

export interface WorkflowExecutionsTableProps {
  sessions: FlowSessionItem[];
  previewLimit?: number;
}

const DEFAULT_PREVIEW_LIMIT = 5;

const STATUS_COLOR: Record<FlowSessionItem['status'], string> = {
  active: '#3B82F6',
  waiting_on_child: '#3B82F6',
  manually_paused: '#F59E0B',
  completed: '#10B981',
  abandoned: '#EF4444',
};

const CHANNEL_ICON: Record<FlowSessionItem['channel'], string> = {
  whatsapp: 'logo-whatsapp',
  facebook_messenger: 'logo-facebook',
  x_twitter: 'close',
};

const CHANNEL_COLOR: Record<FlowSessionItem['channel'], string> = {
  whatsapp: '#25D366',
  facebook_messenger: '#1877F2',
  x_twitter: '#000000',
};

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

/** Flow-session datatable — mirrors UsersTeamCard.tsx's search + preview-
 * slice pattern; status/channel color vocabulary matches the established
 * convention used across PlatformPerformanceCard/FlowSessionsCard. */
export const WorkflowExecutionsTable: React.FC<WorkflowExecutionsTableProps> = ({ sessions, previewLimit = DEFAULT_PREVIEW_LIMIT }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const typo = useTypography();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q ? sessions.filter((s) => s.flowName.toLowerCase().includes(q)) : sessions;
    return base.slice(0, previewLimit);
  }, [sessions, query, previewLimit]);

  return (
    <View style={styles.container}>
      <View style={[styles.searchBox, { flexDirection: rtl.rowDirection }]}>
        <Ionicons name="search-outline" size={14} color={colors.textLight} />
        <TextInput
          style={[styles.searchInput, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]}
          placeholder={t('dashboardAnalytics.workflows.search')}
          placeholderTextColor={colors.textLight}
          value={query}
          onChangeText={setQuery}
        />
      </View>

      <View style={styles.list}>
        {filtered.map((s) => (
          <View key={s.id} style={[styles.row, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name={CHANNEL_ICON[s.channel] as any} size={16} color={CHANNEL_COLOR[s.channel]} />
            <View style={styles.rowContent}>
              <Text style={[styles.flowName, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.medium }]} numberOfLines={1}>
                {s.flowName}
              </Text>
              <Text style={[styles.subtext, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]}>
                {timeAgo(s.lastAdvancedAt)}
              </Text>
            </View>
            <View style={[styles.statusBadge, { backgroundColor: `${STATUS_COLOR[s.status]}20` }]}>
              <Text style={[styles.statusText, { color: STATUS_COLOR[s.status], fontFamily: typo.fontFamily.semibold }]}>
                {t(`dashboardAnalytics.workflows.${s.status}`)}
              </Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    gap: spacing.sm,
  },
  searchBox: {
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.sm,
    paddingHorizontal: spacing.sm,
  },
  searchInput: {
    flex: 1,
    height: 32,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
  },
  list: {
    gap: spacing.sm,
  },
  row: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  rowContent: {
    flex: 1,
    minWidth: 0,
  },
  flowName: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
  },
  subtext: {
    fontSize: 10,
    color: colors.textLight,
  },
  statusBadge: {
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: 999,
  },
  statusText: {
    fontSize: 10,
  },
});
